/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

import { PriceComponent } from '@msdyn365-commerce/components';
import { ChannelIdentity, OrderShipments, OrgUnitLocation, SalesOrder } from '@msdyn365-commerce/retail-proxy';
import { ArrayExtensions } from '@msdyn365-commerce-modules/retail-actions';
import { INodeProps } from '@msdyn365-commerce-modules/utilities';
import get from 'lodash/get';
import QRCode from 'qrcode.react';
import * as React from 'react';

import { countItems, IModuleComponentProps, IPriceContext } from './utilities';

/**
 * Interface for order information input resources.
 */
export interface IOrderInformationInputResources {
    receiptEmailMessage?: string;
    orderItemLabel: string;
    orderItemsLabel: string;
    processingLabel: string;
    orderIdLabel: string;
    receiptIdLabel: string;
    confirmationIdLabel: string;
    trackingLabel: string;
    trackingAriaLabel: string;
    trackingComingSoonLabel: string;
    posChannelNameText?: string;
    onlineStoreChannelNameText?: string;
    orderPlacedOnLabel?: string;
}

/**
 * Interface for order information input.
 */
export interface IOrderInformationInput {
    moduleName: string;
    channelReferenceId?: string;
    channelName?: string;
    channelAddress?: string;
    showChannelInfo?: boolean;
    receiptEmail?: string;
    createdDate?: string;
    amount?: number;
    priceContext: IPriceContext;
    salesId?: string;
    receiptId?: string;
    hasTrackingInfo?: boolean;
    count?: number;
    resources: IOrderInformationInputResources;
    isSalesInvoice?: boolean;
    shouldShowQrCode?: boolean;
}

/**
 * Interface for order information.
 */
export interface IOrderInformation {
    orderInformationProps: INodeProps;
    channelName: React.ReactNode;
    channelAddress: React.ReactNode;
    salesId: React.ReactNode;
    receiptId: React.ReactNode;
    receiptEmail: React.ReactNode;
    createdDate: React.ReactNode;
    count: React.ReactNode;
    amount: React.ReactNode;
    channelReferenceId: React.ReactNode;
}

/**
 * The order confirmation ID node.
 * @param {string | undefined} channelReferenceId The order confirmation id.
 * @param {string} moduleName The module name.
 * @param {string} confirmationIdLabel The confirmation label.
 * @param {boolean | undefined} shouldShowQrCode Should the view show the QR code.
 * @returns {React.ReactNode | undefined} The react node for order confirmation ID.
 */
const getChannelReference = (
    channelReferenceId: string | undefined,
    moduleName: string,
    confirmationIdLabel: string,
    shouldShowQrCode: boolean | undefined): React.ReactNode | undefined => {

    const confirmationIdNode: React.ReactNode = (
        <span className={`${moduleName}__order-information-channel-reference-id`}>
            {`${confirmationIdLabel} `}
            <span>
                {channelReferenceId}
            </span>
        </span>
    );
    return channelReferenceId && (
        (shouldShowQrCode !== undefined && shouldShowQrCode) ? (<div>
            {confirmationIdNode}
            <div className={`${moduleName}__order-information-qr-code`}>
                <QRCode
                    value={channelReferenceId}
                    size={80}
                />
            </div>
        </div>
        ) : confirmationIdNode
    );
};

/**
 * Get order information.
 * @param {IOrderInformationInput} param0 The input for order information.
 * @returns {IOrderInformation} The order information.
 */
export const getOrderInformation = ({
    moduleName,
    channelReferenceId,
    channelName,
    channelAddress,
    showChannelInfo,
    receiptEmail,
    createdDate,
    amount,
    priceContext,
    salesId,
    receiptId,
    count,
    resources: {
        receiptEmailMessage,
        orderItemLabel,
        orderItemsLabel,
        orderIdLabel,
        receiptIdLabel,
        confirmationIdLabel,
        posChannelNameText,
        onlineStoreChannelNameText,
        orderPlacedOnLabel
    },
    isSalesInvoice,
    shouldShowQrCode
}: IOrderInformationInput): IOrderInformation => ({
    orderInformationProps: { className: `${moduleName}__order-information` },
    channelName: showChannelInfo && channelName && (
        channelAddress ? <span className={`${moduleName}__order-information-channel-name`}>
            {`${posChannelNameText} ${channelName}`}
        </span> : <span className={`${moduleName}__order-information-channel-name`}>
            {onlineStoreChannelNameText}
        </span>),
    channelAddress: showChannelInfo && channelAddress && <span className={`${moduleName}__order-information-channel-address`}>
        {channelAddress}
    </span>,
    salesId: salesId ? <span className={`${moduleName}__order-information-sales-id`}>
        {`${orderIdLabel} ${salesId}`}
    </span> : '',
    receiptId: receiptId ? <span className={`${moduleName}__order-information-receipt-id`}>
        {`${receiptIdLabel} ${receiptId}`}
    </span> : '',
    receiptEmail: receiptEmail && (
        <span className={`${moduleName}__order-information-receipt-email`}>
            {receiptEmailMessage}
            <span>
                {receiptEmail}
            </span>
        </span>
    ),
    createdDate: !isSalesInvoice && createdDate && <span className={`${moduleName}__order-information-created-date`}>
        {orderPlacedOnLabel}
        {' '}
        {createdDate}
    </span>,
    count: !isSalesInvoice && count !== undefined && count > 0 && (
        <span className={`${moduleName}__order-information-count`}>
            {count === 1 ? `${count} ${orderItemLabel}` : `${count} ${orderItemsLabel}`}
        </span>
    ),
    amount: !isSalesInvoice && (amount || amount === 0) && (
        <PriceComponent
            {...priceContext}
            className={`${moduleName}__order-information-amount`}
            data={{ price: { CustomerContextualPrice: amount } }}
        />
    ),
    channelReferenceId: getChannelReference(channelReferenceId, moduleName, confirmationIdLabel, shouldShowQrCode)
});

/**
 * Interface for order information props.
 */
interface IProps extends IModuleComponentProps {
    resources: IOrderInformationInputResources;
}

/**
 * Retrieves order channel name.
 * @param order - Order info.
 * @param channelIdentities - Channels data.
 * @returns String with the name of the channel or an empty string.
 */
export const getOrderChannelName: (order: SalesOrder | OrderShipments, channelIdentities?: ChannelIdentity[]) => string = (
    order: SalesOrder | OrderShipments, channelIdentities?: ChannelIdentity[]) => {

    const orderChannel = channelIdentities?.find(channel => {
        return channel.RecordId === order.ChannelId;
    });

    const channelName = orderChannel?.Name ?? '';
    return channelName;
};

/**
 * Retrieves order channel address.
 * @param order - Order info.
 * @param orgUnitLocations - OrgUnitLocation data.
 * @returns String with the address of the channel or an empty string.
 */
export const getOrderChannelAddress: (order: SalesOrder | OrderShipments, orgUnitLocations?: OrgUnitLocation[]) => string = (
    order: SalesOrder | OrderShipments, orgUnitLocations?: OrgUnitLocation[]) => {

    const orderOrgUnitLocation = orgUnitLocations?.find(location => {
        return location.ChannelId === order.ChannelId;
    });

    const channelAddress = orderOrgUnitLocation?.Address ?? '';
    return channelAddress;
};

/**
 * Get order information input.
 * @param {IProps} props The properties.
 * @param {string} moduleName  The module name.
 * @param {SalesOrder | OrderShipments} order The sales order or order shipments.
 * @param {IPriceContext} priceContext The price context.
 * @param {ChannelIdentity[]} channelIdentities A list of channel identities.
 * @param {OrgUnitLocation[]} orgUnitLocations A list of organization unit locations.
 * @param {boolean} showChannelInfo Should the view show channel information.
 * @param {boolean} isSalesInvoice Is this a sales invoice.
 * @param {boolean} shouldShowQrCode Should the view show QR code.
 * @returns {IOrderInformationInput} The order information input.
 */
export const getOrderInfomationInput = (
    props: IProps,
    moduleName: string,
    order: SalesOrder | OrderShipments,
    priceContext: IPriceContext,
    channelIdentities?: ChannelIdentity[],
    orgUnitLocations?: OrgUnitLocation[],
    showChannelInfo?: boolean,
    isSalesInvoice?: boolean,
    shouldShowQrCode?: boolean
): IOrderInformationInput => {
    const {
        resources,
        context: {
            request: {
                apiSettings: { channelId }
            }
        }
    } = props;

    // @ts-expect-error OrderShipments doesn't has ChannelId
    const showTrackingInfo = !!order.ChannelId && order.ChannelId === channelId;

    const locale = get(props, 'context.request.locale') || 'en-US';
    const count = order.SalesLines && ArrayExtensions.hasElements(order.SalesLines) ? countItems(order.SalesLines) : 0;

    return {
        moduleName,
        createdDate: (order.CreatedDateTime && new Date(order.CreatedDateTime).toLocaleDateString(locale)) || '',
        salesId: order.SalesId,
        receiptId: order.ReceiptId,
        showChannelInfo,

        // @ts-expect-error OrderShipments doesn't has ReceiptEmail
        receiptEmail: order.ReceiptEmail,
        channelReferenceId: order.ChannelReferenceId,
        channelName: getOrderChannelName(order, channelIdentities),
        channelAddress: getOrderChannelAddress(order, orgUnitLocations),
        amount: order.TotalAmount ?? 0,
        priceContext,
        count,
        resources,
        isSalesInvoice,
        shouldShowQrCode
    };
};
